/**
 * Call Content Types
 *
 * Signaling messages for WebRTC video/audio calls.
 */

import type { DAGObject, ObjectType } from '../dag/types.js';

/**
 * Call state
 */
export type CallState = 'ringing' | 'active' | 'ended' | 'declined' | 'missed';

/**
 * Call offer content - initiates a call
 */
export interface CallOfferContent {
  /** Recipient's public key */
  recipient: string;
  /** Whether video is requested */
  video: boolean;
  /** Whether audio is requested */
  audio: boolean;
  /** Unique call ID */
  callId: string;
}

/**
 * Call answer content - accepts or declines a call
 */
export interface CallAnswerContent {
  /** Call ID being answered */
  callId: string;
  /** Whether the call was accepted */
  accepted: boolean;
  /** Reason for declining (if not accepted) */
  declineReason?: string;
}

/**
 * Call end content - ends an active call
 */
export interface CallEndContent {
  /** Call ID being ended */
  callId: string;
  /** Duration in seconds */
  duration?: number;
  /** Reason for ending */
  reason?: 'hangup' | 'error' | 'timeout';
}

/**
 * Type guards
 */
export function isCallOffer(obj: DAGObject): obj is DAGObject<CallOfferContent> {
  return obj.type === 'call.offer';
}

export function isCallAnswer(obj: DAGObject): obj is DAGObject<CallAnswerContent> {
  return obj.type === 'call.answer';
}

export function isCallEnd(obj: DAGObject): obj is DAGObject<CallEndContent> {
  return obj.type === 'call.end';
}

/**
 * Create a call offer
 */
export function createCallOffer(
  recipient: string,
  options: { video?: boolean; audio?: boolean } = {}
): { type: ObjectType; content: CallOfferContent } {
  const callId = `call-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return {
    type: 'call.offer' as ObjectType,
    content: {
      recipient,
      video: options.video ?? false,
      audio: options.audio ?? true,
      callId,
    },
  };
}

/**
 * Create a call answer
 */
export function createCallAnswer(
  callId: string,
  accepted: boolean,
  declineReason?: string
): { type: ObjectType; content: CallAnswerContent } {
  return {
    type: 'call.answer' as ObjectType,
    content: {
      callId,
      accepted,
      declineReason,
    },
  };
}

/**
 * Create a call end
 */
export function createCallEnd(
  callId: string,
  duration?: number,
  reason: 'hangup' | 'error' | 'timeout' = 'hangup'
): { type: ObjectType; content: CallEndContent } {
  return {
    type: 'call.end' as ObjectType,
    content: {
      callId,
      duration,
      reason,
    },
  };
}
